import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiLightbulb, FiX, FiArrowRight, FiCheck } = FiIcons;

const FeatureHighlight = ({ features = [], onComplete, autoAdvance = true, interval = 5000 }) => {
  const [currentFeature, setCurrentFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!autoAdvance || isPaused || features.length === 0) return;

    const timer = setInterval(() => {
      setCurrentFeature(prev => {
        if (prev < features.length - 1) {
          return prev + 1;
        } else {
          setIsVisible(false);
          setTimeout(onComplete, 300);
          return prev;
        }
      });
    }, interval);

    return () => clearInterval(timer);
  }, [autoAdvance, isPaused, features.length, interval, onComplete]);

  const nextFeature = () => {
    if (currentFeature < features.length - 1) {
      setCurrentFeature(currentFeature + 1);
    } else {
      setIsVisible(false);
      setTimeout(onComplete, 300);
    }
  };

  const prevFeature = () => {
    if (currentFeature > 0) {
      setCurrentFeature(currentFeature - 1);
    }
  };

  const close = () => {
    setIsVisible(false);
    setTimeout(onComplete, 300);
  };

  if (!features.length || !isVisible) return null;

  const feature = features[currentFeature];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-4 right-4 z-50 max-w-sm"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm border border-forge-400/30 rounded-2xl shadow-dominance overflow-hidden">
            {/* Header */}
            <div className={`bg-gradient-to-r ${feature.gradient || 'from-forge-400 to-orange-500'} p-4 text-white`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={feature.icon || FiLightbulb} className="text-lg" />
                  <span className="font-semibold text-sm">Tip</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs opacity-80">
                    {currentFeature + 1} of {features.length}
                  </span>
                  <button
                    onClick={close}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                  >
                    <SafeIcon icon={FiX} className="text-sm" />
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <h4 className="font-semibold text-forge-400 mb-2">
                {feature.title}
              </h4>
              <p className="text-forge-200 text-sm mb-4 leading-relaxed">
                {feature.description}
              </p>

              {/* Progress */}
              <div className="mb-4">
                <div className="w-full bg-shadow-800/50 rounded-full h-1">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${((currentFeature + 1) / features.length) * 100}%` }}
                    transition={{ duration: 0.3 }}
                    className={`h-1 rounded-full bg-gradient-to-r ${feature.gradient || 'from-forge-400 to-orange-500'}`}
                  />
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center">
                <button
                  onClick={prevFeature}
                  disabled={currentFeature === 0}
                  className="text-forge-400 hover:text-forge-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Previous
                </button>
                
                <button
                  onClick={nextFeature}
                  className={`px-4 py-2 bg-gradient-to-r ${feature.gradient || 'from-forge-400 to-orange-500'} text-white rounded-lg hover:shadow-lg transition-all duration-300 flex items-center space-x-2 text-sm`}
                >
                  <span>{currentFeature === features.length - 1 ? 'Got it' : 'Next'}</span>
                  <SafeIcon icon={currentFeature === features.length - 1 ? FiCheck : FiArrowRight} className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeatureHighlight;